import { observable, action, computed } from 'mobx'
import { uniqueId } from 'lodash-es'
import {
  interval,
  Subject,
  merge,
  defer,
  type Observable,
  combineLatest,
  filter,
} from 'rxjs'
import { takeUntil, skipUntil, switchMap, share, take } from 'rxjs/operators'
import { shareLatest, unpair } from '@/utils/rx-util'
import { isNotNil } from '@/utils/object-util'
import { fromStream, toStream } from '@/utils/mobx-util'
import { extractMessageFromError } from '@/utils/error-util'
import {
  type SnackbarAction,
  type SnackbarHandle,
  type SnackbarType,
} from './index'

/**
 * Snackbar model.
 */
export class Snackbar {
  /**
   * Used for starting an Auto Dismiss timer.
   */
  private _autoDismissSubject = new Subject<number>()

  /**
   * Used for notifying when to cancel auto-dismiss.
   */
  private _dismissSubject = new Subject()

  /**
   * Used for notifying when to show the snackbar.
   */
  private _showSubject = new Subject()

  /**
   * Used for triggering actions.
   */
  private _actionSubject = new Subject<SnackbarAction>()

  /**
   * Used for dismissing the snackbar from an external source.
   */
  private _externalDismissSignalSubject = new Subject<Observable<unknown>>()

  /**
   * Snackbar ID.
   */
  readonly id = uniqueId('snackbar-')

  /**
   * Used for deduplication.
   */
  key: symbol | string | null = null

  /**
   * The snackbar title.
   */
  @observable private accessor _title: string | (() => string) = ''

  /**
   * The snackbar description.
   */
  @observable private accessor _description: string | (() => string) = ''

  /**
   * Determines whether the snackbar is auto-dismissible.
   * Used to stop auto-dismissal on focus.
   */
  @observable private accessor _autoDismissible = true

  /**
   * The snackbar type.
   */
  @observable accessor type: SnackbarType = 'neutral'

  /**
   * Snackbar actions.
   */
  @observable accessor actions: SnackbarAction[] = []

  /**
   * Emits when the snackbar is dismissed.
   */
  readonly dismissed$: Observable<unknown>

  /**
   * Holds values from RxJS streams.
   */
  private readonly $

  /**
   * Creates an Snackbar.
   *
   * @param onShow
   * @param onDismiss
   */
  constructor(onShow: () => unknown, onDismiss: () => unknown) {
    // Show the snackbar, but...
    const show$ = this._showSubject.pipe(
      // * never after having been dismissed
      takeUntil(defer(() => dismiss$)),
      // * and only show once
      take(1),
      share(),
    )

    // Handle actions while showing, until dismissed.
    const action$ = this._actionSubject.pipe(skipUntil(show$), take(1), share())

    // Cancel auto-dismissal when we're no longer allowed to auto-dismiss.
    const autoDismissible$ = toStream(() => this._autoDismissible, true)
    const cancelAutoDismiss$ = autoDismissible$.pipe(filter((x) => !x))

    // Dismiss the snackbar when...
    const dismiss$: Observable<unknown> = merge(
      // * explicitly told to dismiss
      this._dismissSubject,
      // * interacting with the snackbar
      action$,
      // * auto-dismiss has been requested...
      //   (only start the auto dismiss timer after having been shown)
      combineLatest([this._autoDismissSubject, show$, autoDismissible$]).pipe(
        unpair(),
        switchMap((ms) =>
          // and the timer elapsed
          interval(ms).pipe(
            // but if we cancel auto dismiss, stop
            takeUntil(cancelAutoDismiss$),
            // and we only care about the first emission of the interval
            take(1),
          ),
        ),
      ),
      // * external dismiss signal
      this._externalDismissSignalSubject.pipe(switchMap((signal) => signal)),
    ).pipe(
      // ... but only after having been shown.
      skipUntil(show$),
      // and only dismiss once.
      take(1),
      share(),
    )

    action$.subscribe((a) => a.handler())
    show$.subscribe(onShow)
    dismiss$.subscribe(onDismiss)

    this.dismissed$ = dismiss$

    // Ensures that the title doesn't change after the snackbar is dismissed.
    const title$ = toStream(
      () => (this._title instanceof Function ? this._title() : this._title),
      true,
    ).pipe(takeUntil(dismiss$), shareLatest())

    // Ensures that the description doesn't change after the snackbar is dismissed.
    const description$ = toStream(
      () =>
        this._description instanceof Function
          ? this._description()
          : this._description,
      true,
    ).pipe(takeUntil(dismiss$), shareLatest())

    this.$ = {
      title: fromStream('', title$),
      description: fromStream('', description$),
    }
  }

  /**
   * The toast's title.
   */
  @computed
  get title() {
    return this.$.title()
  }

  /**
   * The toast's description.
   */
  @computed
  get description() {
    return this.$.description()
  }

  /**
   * Changes the title and description of the snackbar.
   *
   * @param title the new title.
   * @param description the new description. If omitted, sets to an empty string.
   */
  @action.bound
  saying(
    title: string | (() => string),
    description?: string | (() => string),
  ): this {
    this._title = title
    this._description = description || ''

    return this
  }

  /**
   * Sets the snackbar type.
   */
  @action.bound
  setType(type: SnackbarType): this {
    this.type = type
    return this
  }

  /**
   * Specifies up to 2 possible actions to take.
   */
  @action.bound
  setActions(
    primaryAction?: SnackbarAction,
    secondaryAction?: SnackbarAction,
  ): this {
    this.actions = [primaryAction, secondaryAction].filter(isNotNil)
    return this
  }

  /**
   * Sets the key used for deduplication.
   *
   * @param key
   */
  withKey(key: symbol | string | null) {
    this.key = key
    return this
  }

  /**
   * Populates the snackbar title and description from an error.
   *
   * @param err
   */
  @action.bound
  fromError(err: unknown) {
    const messageFromError = extractMessageFromError(err)
    const [firstPart, ...rest] = messageFromError.split(':')
    return this.setType('error')
      .saying(firstPart.trim(), rest.join(':').trim())
      .show()
  }

  /**
   * Shows the snackbar until the given observable signal emits.
   *
   * @param signal
   */
  @action.bound
  until(signal: Observable<unknown>): this {
    this._externalDismissSignalSubject.next(signal)
    return this
  }

  /**
   * Schedules the snackbar to auto-dismiss after the specified amount of milliseconds.
   *
   * @param ms
   */
  @action.bound
  autoDismiss(ms = 6000) {
    this._autoDismissSubject.next(ms)
    return this
  }

  /**
   * Shows the built snackbar.
   */
  @action.bound
  show(): this {
    this._showSubject.next(undefined)
    this._showSubject.complete()
    return this
  }

  /**
   * Dismisses the snackbar. Once dismissed, cannot be shown again.
   */
  @action.bound
  dismiss(): this {
    this._dismissSubject.next(undefined)
    this._dismissSubject.complete()
    return this
  }

  /**
   * Cancels any pending auto dismissal and disallows it from triggering.
   */
  @action.bound
  disallowAutoDismiss() {
    this._autoDismissible = false
    return this
  }

  /**
   * Allows for auto-dismissal again.
   */
  @action.bound
  allowAutoDismissal() {
    this._autoDismissible = true
    return this
  }

  /**
   * Triggers the specified action and dismisses the Snackbar.
   *
   * @param snackbarAction
   */
  @action.bound
  triggerAction(snackbarAction: SnackbarAction): this {
    this._actionSubject.next(snackbarAction)
    this._actionSubject.complete()
    return this
  }
}

/**
 * Wraps a snackbar in a handle to limit the exposed interface.
 */
export class SnackbarHandleImpl implements SnackbarHandle {
  dismissed$: Observable<unknown>

  constructor(private snackbar: Snackbar) {
    this.dismissed$ = snackbar.dismissed$
  }

  actions(
    primaryAction?: SnackbarAction,
    secondaryAction?: SnackbarAction,
  ): SnackbarHandle {
    this.snackbar.setActions(primaryAction, secondaryAction)
    return this
  }

  /** {@inheritDoc} */
  autoDismiss(duration?: number): SnackbarHandle {
    this.snackbar.autoDismiss(duration)
    return this
  }

  /** {@inheritDoc} */
  until(signal: Observable<unknown>): SnackbarHandle {
    this.snackbar.until(signal)
    return this
  }
  /** {@inheritDoc} */
  dismiss(): SnackbarHandle {
    this.snackbar.dismiss()
    return this
  }

  /** {@inheritDoc} */
  saying(
    title: string | (() => string),
    description?: string | (() => string),
  ): SnackbarHandle {
    this.snackbar.saying(title, description)
    return this
  }

  /** {@inheritDoc} */
  show(): SnackbarHandle {
    this.snackbar.show()
    return this
  }

  /** {@inheritDoc} */
  type(type: SnackbarType): SnackbarHandle {
    this.snackbar.setType(type)
    return this
  }

  /** {@inheritDoc} */
  @action.bound
  update(updater: (handle: SnackbarHandle) => unknown): SnackbarHandle {
    updater(this)
    return this
  }

  /** {@inheritDoc} */
  withKey(key: symbol | string | null): SnackbarHandle {
    this.snackbar.withKey(key)
    return this
  }

  /** {@inheritDoc} */
  @action.bound
  fromError(err: unknown): SnackbarHandle {
    this.snackbar.fromError(err)
    return this
  }
}
